import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Toster from "../../common/Toster";
import Tooltip from "../../common/Tooltip";

// Import stage management utilities
import {
  STAGES,
  validateStage,
  getNextStage,
  getStageData,
} from "../../../utils/stageManagement";

// Import tab components
import GeneralInfo from "./AddPackageTabs/GeneralInfo";
import ItineraryInfo from "./AddPackageTabs/ItinearyInfo";
import StayInfo from "./AddPackageTabs/StayInfo";
import Inclusions from "./AddPackageTabs/Inclusions";
import PricingInfo from "./AddPackageTabs/Pricing";
import AdditionalInfo from "./AddPackageTabs/AdditionalInfo";
import TermsAndCondition from "./AddPackageTabs/TermsAndCondition";
import CancellationInfo from "./AddPackageTabs/Cancellation";
import Faq from "./AddPackageTabs/Faq";

// Import services
import {
  createGeneralInfo,
  updateGeneralInfo,
  updateItineraryStageStatus,
  packageStaySaveContinue,
  saveInclusionData,
  updateInclusionData,
  saveAndContinuePricing,
  updateAdditionalInfo,
  updateTermsAndConditions,
  createCancellationPolicy,
  updateCancellationPolicy,
  addFaq,
  updateFaq,
} from "./packageService";

import {
  navItems,
  isSectionCompleted,
  getNextSectionId,
  isOptionalPackageSection,
} from "./packageWizardConfig";
import {
  getPackageCompletedStage,
  isPackageSectionAccessible,
} from "./packageWorkflowUtils";
import { getSectionTooltipMessage } from "./packageNavTooltips";
import PackageBuilderNav from "./PackageBuilderNav";
import PackageCompletionTracker from "./PackageCompletionTracker";
import PackageActionBar from "./PackageActionBar";
import PackageItineraryDownload from "./PackageItineraryDownload";
import { usePackageSectionSpy } from "./usePackageSectionSpy";
import { useSectionDirtyGuard } from "../../../utils/useSectionDirtyGuard";
import { isEditableElement } from "../../../utils/builderInputUtils";
import UnsavedSectionDialog from "../../common/UnsavedSectionDialog";

const SECTION_IDS = navItems.map((item) => item.id);

const AddPackage = ({
  onClose,
  onPackageCreated,
  backLabel = "Back",
  packageId: packageIdProp = null,
  addSubPackageId,
  restrictedTourType,
  setRestrictedTourType,
  selectedGroupPackageCount,
  setSelectedGroupPackageCount,
  initialTab = null,
}) => {
  const [localPackageId, setLocalPackageId] = useState(null);
  const effectivePackageId = packageIdProp ?? localPackageId;
  const [activeTab, setActiveTab] = useState("general");
  const [editingSectionId, setEditingSectionId] = useState(null);
  const editingSectionRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const initialLoadRef = useRef(true);
  const userNavigatedRef = useRef(false);
  const hasAutoScrolledRef = useRef(false);
  const pinnedSectionRef = useRef(null);
  const manualSectionRef = useRef(null);
  const layoutSyncSectionRef = useRef(null);
  const pinSectionTimerRef = useRef(null);
  const scrollSyncTimersRef = useRef([]);
  const resizeObserverRef = useRef(null);
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const isScrollingRef = useRef(false);
  const pricingRef = useRef(null);
  const itineraryRef = useRef(null);
  const stayRef = useRef(null);
  const [collapsedSections, setCollapsedSections] = useState(() =>
    navItems.filter((item) => item.defaultCollapsed).map((item) => item.id)
  );
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [generalInfoData, setGeneralInfoData] = useState(null);
  const [itineraryData, setItineraryData] = useState(null);
  const [stayData, setStayData] = useState(null);
  const [inclusionData, setInclusionData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [additionalInfoData, setAdditionalInfoData] = useState(null);
  const [termsConditionData, setTermsConditionData] = useState(null);
  const [cancellationData, setCancellationData] = useState(null);
  const [faqData, setFAQData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingSectionId, setSavingSectionId] = useState(null);
  const [showToster, setShowToster] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const queryClient = useQueryClient();

  // Loading state handler
  const handleTabLoading = (loading) => {
    setIsTabLoading(loading);
  };

  useEffect(() => {
    setIsTabLoading(false);
  }, [activeTab]);

  const expandSection = (sectionId) => {
    setCollapsedSections((prev) => prev.filter((id) => id !== sectionId));
  };

  const clearScrollSyncTimers = () => {
    scrollSyncTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    scrollSyncTimersRef.current = [];
  };

  const disconnectSectionResizeObserver = () => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
  };

  const scrollSectionToTop = (sectionId, behavior = "auto") => {
    const section = sectionRefs.current[sectionId];
    if (!section) return;

    const offset = 160;
    const top =
      section.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior });
  };

  const observeSectionResize = () => {
    disconnectSectionResizeObserver();
    // Intentionally no-op: scrolling on resize yanked the viewport to the section
    // header whenever "Add more" (or similar) expanded the section.
  };

  const scheduleLayoutSyncScroll = () => {
    const timerId = window.setTimeout(() => {
      if (layoutSyncSectionRef.current) {
        scrollSectionToTop(layoutSyncSectionRef.current, "auto");
      }
    }, 120);
    scrollSyncTimersRef.current.push(timerId);
  };

  const endLayoutSync = () => {
    layoutSyncSectionRef.current = null;
    disconnectSectionResizeObserver();
  };

  const pinActiveSection = useCallback((sectionId, durationOrOptions = 4000) => {
    const options =
      typeof durationOrOptions === "number"
        ? { durationMs: durationOrOptions }
        : durationOrOptions;
    const { durationMs = 4000, scrollIntoView = false } = options;

    if (pinSectionTimerRef.current) {
      window.clearTimeout(pinSectionTimerRef.current);
    }
    userNavigatedRef.current = true;
    manualSectionRef.current = sectionId;
    pinnedSectionRef.current = sectionId;
    editingSectionRef.current = null;
    setEditingSectionId(null);
    setActiveTab(sectionId);
    endLayoutSync();

    if (scrollIntoView) {
      const section = sectionRefs.current[sectionId];
      if (section) {
        const rect = section.getBoundingClientRect();
        const offset = 160;
        const isInView = rect.top <= offset + 40 && rect.bottom > offset + 40;
        if (!isInView) {
          isScrollingRef.current = true;
          scrollSectionToTop(sectionId, "auto");
          window.setTimeout(() => {
            isScrollingRef.current = false;
          }, 600);
        }
      }
    }

    pinSectionTimerRef.current = window.setTimeout(() => {
      if (manualSectionRef.current === sectionId) {
        manualSectionRef.current = null;
      }
      if (pinnedSectionRef.current === sectionId) {
        pinnedSectionRef.current = null;
      }
      pinSectionTimerRef.current = null;
    }, durationMs);
  }, []);

  const ITINERARY_SCROLL_LOCK_MS = 1400;

  const scrollToItinerarySectionTop = useCallback(
    (behavior = "smooth") => {
      pinActiveSection("itinerary", { durationMs: 15000 });
      isScrollingRef.current = true;
      manualSectionRef.current = "itinerary";

      const align = () => scrollSectionToTop("itinerary", behavior);

      requestAnimationFrame(() => {
        requestAnimationFrame(align);
      });

      window.setTimeout(() => {
        isScrollingRef.current = false;
      }, ITINERARY_SCROLL_LOCK_MS);
    },
    [pinActiveSection]
  );

  const scrollToItineraryDay = useCallback(
    (dayId) => {
      pinActiveSection("itinerary", { durationMs: 15000 });
      isScrollingRef.current = true;
      manualSectionRef.current = "itinerary";

      const align = () => {
        const section = sectionRefs.current.itinerary;
        const dayEl = document.getElementById(`itinerary-day-${dayId}`);
        if (!section || !dayEl) {
          scrollSectionToTop("itinerary", "smooth");
          return;
        }

        const offset = 160;
        const sectionTop =
          section.getBoundingClientRect().top + window.scrollY - offset;
        const dayTop =
          dayEl.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: Math.max(0, Math.max(sectionTop, dayTop)),
          behavior: "smooth",
        });
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(align);
      });

      window.setTimeout(() => {
        isScrollingRef.current = false;
      }, ITINERARY_SCROLL_LOCK_MS);
    },
    [pinActiveSection]
  );

  const bootstrapPackageId = packageIdProp ?? null;
  const completedStage = getPackageCompletedStage(generalInfoData);
  const generalSectionSaved = Boolean(effectivePackageId);

  const scrollToSection = (sectionId, { userInitiated = false } = {}) => {
    if (userInitiated) {
      userNavigatedRef.current = true;
      initialLoadRef.current = false;
      manualSectionRef.current = sectionId;
      layoutSyncSectionRef.current = sectionId;
      clearScrollSyncTimers();
      disconnectSectionResizeObserver();
    }

    expandSection(sectionId);
    editingSectionRef.current = null;
    setEditingSectionId(null);
    setActiveTab(sectionId);
    pinnedSectionRef.current = sectionId;

    const section = sectionRefs.current[sectionId];
    if (section) {
      isScrollingRef.current = true;
      const behavior = userInitiated ? "auto" : "smooth";

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollSectionToTop(sectionId, behavior);

          if (userInitiated) {
            observeSectionResize();
            scheduleLayoutSyncScroll();
            const endTimerId = window.setTimeout(endLayoutSync, 400);
            scrollSyncTimersRef.current.push(endTimerId);
          }
        });
      });

      const scrollLockMs = userInitiated ? 700 : 1000;
      window.setTimeout(() => {
        isScrollingRef.current = false;
        if (manualSectionRef.current === sectionId || !userInitiated) {
          setActiveTab(sectionId);
        }
        window.setTimeout(() => {
          if (pinnedSectionRef.current === sectionId) {
            pinnedSectionRef.current = null;
          }
          if (userInitiated && manualSectionRef.current === sectionId) {
            manualSectionRef.current = null;
          }
        }, userInitiated ? 400 : 200);
      }, scrollLockMs);
    }
  };

  const handleSpyActiveChange = useCallback((sectionId) => {
    if (manualSectionRef.current) return;
    if (editingSectionRef.current) return;
    if (isScrollingRef.current || pinnedSectionRef.current) return;
    setActiveTab(sectionId);
  }, []);

  useEffect(() => {
    editingSectionRef.current = editingSectionId;
  }, [editingSectionId]);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  const displaySectionId = editingSectionId || activeTab;

  const saveBeforeLeaveRef = useRef(null);

  const {
    dialogOpen: unsavedDialogOpen,
    leaveDialogOpen,
    requestSectionNavigation,
    cancelPendingNavigation,
    completePendingNavigation,
    discardPendingNavigation,
    requestLeave,
    cancelLeave,
    confirmLeaveSave,
    discardLeave,
    clearSectionDirty,
    isActiveSectionDirty,
    getSectionCaptureProps,
    enhanceSectionChild,
  } = useSectionDirtyGuard({
    activeSectionId: displaySectionId,
    isLoading: isTabLoading,
    getSaveBeforeLeave: () => saveBeforeLeaveRef.current,
  });

  const handleSectionNavClick = (sectionId, stage) => {
    if (!generalSectionSaved && sectionId !== "general") {
      setShowToster({
        message: "Please save General Information first",
        type: "warning",
        show: true,
      });
      return;
    }

    if (
      !isPackageSectionAccessible(stage, completedStage, generalSectionSaved) &&
      sectionId !== "general"
    ) {
      setShowToster({
        message: "Complete earlier sections first to unlock this stage",
        type: "warning",
        show: true,
      });
      return;
    }

    requestSectionNavigation(sectionId, () => {
      scrollToSection(sectionId, { userInitiated: true });
    });
  };

  const activeNavItem = navItems.find((item) => item.id === displaySectionId);
  const activeSectionLabel = activeNavItem?.name || "Section";
  const isActiveSectionDisabled =
    !activeNavItem ||
    (!generalSectionSaved && displaySectionId !== "general") ||
    (displaySectionId !== "general" &&
      !isPackageSectionAccessible(
        activeNavItem.stage,
        completedStage,
        generalSectionSaved
      ));

  const isEditMode = Boolean(packageIdProp);
  const noChangesToSave =
    isEditMode && !isActiveSectionDisabled && !isActiveSectionDirty();

  useEffect(() => {
    if (packageIdProp) {
      setLocalPackageId(packageIdProp);
    }
  }, [packageIdProp]);

  // Handle close and clear cache
  const performClose = () => {
    queryClient.clear();

    setActiveTab("general");
    setEditingSectionId(null);
    editingSectionRef.current = null;
    setGeneralInfoData(null);
    setItineraryData(null);
    setStayData(null);
    setInclusionData(null);
    setPricingData(null);
    setAdditionalInfoData(null);
    setTermsConditionData(null);
    setCancellationData(null);
    setFAQData(null);
    setValidationErrors(null);
    userNavigatedRef.current = false;
    hasAutoScrolledRef.current = false;
    initialLoadRef.current = true;
    pinnedSectionRef.current = null;
    manualSectionRef.current = null;
    layoutSyncSectionRef.current = null;
    if (pinSectionTimerRef.current) {
      window.clearTimeout(pinSectionTimerRef.current);
      pinSectionTimerRef.current = null;
    }
    clearScrollSyncTimers();
    disconnectSectionResizeObserver();

    if (typeof setRestrictedTourType === "function") {
      setRestrictedTourType(null);
    }

    if (typeof setSelectedGroupPackageCount === "function") {
      setSelectedGroupPackageCount(null);
    }

    onClose();
  };

  const handleClose = () => {
    if (!isActiveSectionDirty()) {
      performClose();
      return;
    }
    requestLeave(performClose);
  };

  // Update stage when generalInfoData loads; auto-scroll only once if user hasn't navigated
  useEffect(() => {
    if (!generalInfoData?.current_stage) return;

    const stage = generalInfoData.current_stage;

    if (userNavigatedRef.current || hasAutoScrolledRef.current) return;

    hasAutoScrolledRef.current = true;
    initialLoadRef.current = false;

    if (stage < STAGES.FAQ) {
      const currentTab = navItems.find((item) => item.stage === stage);
      if (currentTab) {
        window.setTimeout(() => {
          if (userNavigatedRef.current) return;
          scrollToSection(currentTab.id);
        }, 150);
      }
    } else {
      setActiveTab("general");
      setEditingSectionId(null);
    }
  }, [generalInfoData?.current_stage, generalInfoData?.id]);

  // When editing an existing package, expand optional sections that are complete or have saved data
  useEffect(() => {
    if (!bootstrapPackageId) return;

    const stage = Number(generalInfoData?.current_stage) || 0;

    setCollapsedSections((prev) =>
      prev.filter((sectionId) => {
        const item = navItems.find((nav) => nav.id === sectionId);
        if (!item?.defaultCollapsed) return true;

        if (stage && isSectionCompleted(stage, item.stage)) {
          return false;
        }

        if (
          sectionId === "additional" &&
          additionalInfoData?.additional_info?.trim()
        ) {
          return false;
        }

        if (
          sectionId === "terms" &&
          termsConditionData?.terms_and_conditions?.trim()
        ) {
          return false;
        }

        if (sectionId === "cancellation") {
          const hasRules = cancellationData?.data?.some(
            (rule) =>
              rule.cancellation_days ||
              rule.cancellation_days_from ||
              rule.refund_percentage
          );
          if (hasRules || cancellationData?.isUpdate) {
            return false;
          }
        }

        return true;
      })
    );
  }, [
    bootstrapPackageId,
    generalInfoData?.current_stage,
    additionalInfoData?.additional_info,
    termsConditionData?.terms_and_conditions,
    cancellationData,
  ]);

  useEffect(() => {
    initialLoadRef.current = true;
    if (initialTab) {
      userNavigatedRef.current = true;
      hasAutoScrolledRef.current = true;
      window.setTimeout(
        () => scrollToSection(initialTab, { userInitiated: true }),
        200
      );
    }
  }, [initialTab]);

  usePackageSectionSpy({
    sectionIds: SECTION_IDS,
    sectionRefs,
    isScrollingRef,
    pinnedSectionRef,
    editingSectionRef,
    isSubmittingRef,
    onActiveChange: handleSpyActiveChange,
    offset: 160,
    enabled: true,
  });

  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!(target instanceof HTMLElement)) return false;
      return Boolean(
        target.closest(
          "input, select, textarea, [contenteditable='true'], [role='listbox'], [data-prevent-section-spy='true']"
        )
      );
    };

    const releaseManualSection = (event) => {
      if (isScrollingRef.current || pinnedSectionRef.current) return;
      if (event?.target instanceof HTMLElement) {
        if (event.target.closest("[data-modal-overlay='true']")) return;
        if (event.target.closest("[data-prevent-section-scroll='true']")) return;
        if (isEditableTarget(event.target)) return;
      }
      if (isEditableTarget(document.activeElement)) return;
      manualSectionRef.current = null;
      editingSectionRef.current = null;
      setEditingSectionId(null);
      endLayoutSync();
    };

    const handleFocusIn = (event) => {
      if (!contentRef.current?.contains(event.target)) return;
      const sectionEl = event.target?.closest?.("[data-section-id]");
      const sectionId = sectionEl?.dataset?.sectionId;
      if (!sectionId) return;
      manualSectionRef.current = sectionId;
      editingSectionRef.current = sectionId;
      queueMicrotask(() => {
        setEditingSectionId(sectionId);
      });
      // Do not call setActiveTab here — it re-renders the builder and drops the first keystroke.
    };

    const handleKeyDown = (event) => {
      if (
        [
          "PageUp",
          "PageDown",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      ) {
        if (!isEditableTarget(event.target)) {
          releaseManualSection();
        }
      }
    };

    const handleContentPointerDown = (event) => {
      if (!contentRef.current?.contains(event.target)) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest(
          "button, [role='button'], [data-prevent-section-scroll='true']"
        )
      ) {
        endLayoutSync();
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("pointerdown", handleContentPointerDown, true);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("pointerdown", handleContentPointerDown, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Mutations setup
  const generalInfoMutation = useMutation({
    mutationFn: createGeneralInfo,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["generalInfo", effectivePackageId]);
      queryClient.invalidateQueries(["packages"]);
      const newId = response.data.id;
      setLocalPackageId(newId);
      if (onPackageCreated) {
        onPackageCreated(newId);
      }
    },
    onError: (error) => {
      console.error("Error saving package:", error);
    },
  });

  // Update General Info Mutation
  const updateGeneralInfoMutation = useMutation({
    mutationFn: ({ packageId, formData }) =>
      updateGeneralInfo(packageId, formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["generalInfo", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error updating general info:", error);
    },
  });

  const advancePackageStage = async (stage, isDraft) => {
    const response = await updateItineraryStageStatus({
      package_id: effectivePackageId,
      current_stage: stage,
      is_draft: isDraft,
    });
    const persistedStage =
      Number(response?.data?.current_stage) || Number(stage);
    setGeneralInfoData((prev) => ({
      ...prev,
      current_stage: Math.max(
        Number(prev?.current_stage) || 0,
        persistedStage
      ),
    }));
    queryClient.invalidateQueries(["generalInfo", effectivePackageId]);
  };

  // stage 2
  // Itinerary Mutation
  const itineraryMutation = useMutation({
    mutationFn: updateItineraryStageStatus,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["itinerary", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error saving itinerary:", error);
    },
  });

  const handleItineraryStageComplete = useCallback(async () => {
    if (!effectivePackageId) return;

    const savedStage = Number(generalInfoData?.current_stage) || 0;
    const highestCompletedStage = Math.max(savedStage, getNextStage(STAGES.ITINERARY));

    try {
      userNavigatedRef.current = true;
      await itineraryMutation.mutateAsync({
        package_id: effectivePackageId,
        current_stage: highestCompletedStage,
        is_draft: 1,
      });

      setGeneralInfoData((prev) => ({
        ...prev,
        current_stage: highestCompletedStage,
      }));
      scrollToSection("stay", { userInitiated: true });
      setLastSavedAt(new Date());
      setShowToster({
        message: "All itinerary days saved. Continue with Stay Information.",
        type: "success",
        show: true,
      });
    } catch (error) {
      setShowToster({
        message: error.message || "Failed to complete itinerary stage",
        type: "error",
        show: true,
      });
    }
  }, [
    effectivePackageId,
    generalInfoData?.current_stage,
    itineraryMutation,
    scrollToSection,
  ]);

  // stage 3
  // Stay Mutation
  const stayMutation = useMutation({
    mutationFn: packageStaySaveContinue,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["stay", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error saving stay:", error);
    },
  });

  // stage 4
  // update inclusion mutation
  const updateInclusionMutation = useMutation({
    mutationFn: ({ packageId, formData }) =>
      updateInclusionData(packageId, formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["inclusion", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error updating inclusion:", error);
    },
  });

  // Inclusion Mutation
  const inclusionMutation = useMutation({
    mutationFn: saveInclusionData,
    onSuccess: (response) => {
      queryClient.invalidateQueries(["inclusion", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error saving inclusion:", error);
    },
  });

  // save and continue pricing
  const saveAndContinuePricingMutation = useMutation({
    mutationFn: saveAndContinuePricing,
    onSuccess: (response) => {
      const savedStageFromApi = Number(response?.data?.current_stage);
      if (savedStageFromApi) {
        setGeneralInfoData((prev) => ({
          ...prev,
          current_stage: Math.max(
            Number(prev?.current_stage) || 0,
            savedStageFromApi
          ),
        }));
      }
      queryClient.invalidateQueries(["pricing", effectivePackageId]);
      queryClient.invalidateQueries(["generalInfo", effectivePackageId]);
    },
  });

  // additional info mutation
  const updateAdditionalInfoMutation = useMutation({
    mutationFn: ({ packageId, formData }) =>
      updateAdditionalInfo(packageId, formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["additionalInfo", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error updating additional info:", error);
    },
  });

  // terms and condition mutation
  const updateTermsandConditionMutation = useMutation({
    mutationFn: ({ packageId, formData }) =>
      updateTermsAndConditions(packageId, formData),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["termsAndConditions", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error updating additional info:", error);
    },
  });

  // cancellation mutation
  const cancellationMutation = useMutation({
    mutationFn: ({ packageId, data }) => {
      if (data.isUpdate) {
        return updateCancellationPolicy(packageId, data);
      }
      return createCancellationPolicy(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(["cancellation", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error saving cancellation policy:", error);
    },
  });

  // faq mutation
  const addFaqMutation = useMutation({
    mutationFn: ({ packageId, obj }) => addFaq(packageId, obj),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["faq", effectivePackageId]);
    },
    onError: (error) => {
      console.error("Error adding faq:", error);
    },
  });

  const updateFaqMutation = useMutation({
    mutationFn: ({ packageId, obj }) => updateFaq(packageId, obj),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["faq", effectivePackageId]);
    },
  });

  const handleSave = async (sectionId = displaySectionId, options = {}) => {
    const { skipAutoAdvance = false } = options;
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    setSavingSectionId(sectionId);
    setValidationErrors(null);
    manualSectionRef.current = sectionId;
    editingSectionRef.current = sectionId;
    setEditingSectionId(sectionId);
    setActiveTab(sectionId);
    pinnedSectionRef.current = sectionId;
    isScrollingRef.current = true;

    try {
      const currentTabStage = navItems.find(
        (item) => item.id === sectionId
      )?.stage;
      if (!currentTabStage) {
        setShowToster({
          message: "Invalid tab selected",
          type: "error",
          show: true,
        });
        setIsSubmitting(false);
        return false;
      }

      // Get current stage data
      const stageData = getStageData(currentTabStage, {
        generalInfoData,
        itineraryData,
        stayData,
        inclusionData,
        pricingData,
        additionalInfoData,
        termsConditionData,
        cancellationData,
        faqData,
      });

      // Validate current stage
      if (currentTabStage === STAGES.PRICING) {
        const pricingValidation = pricingRef.current?.validateForSave?.();
        if (!pricingValidation?.valid) {
          setShowToster({
            message:
              pricingValidation?.message ||
              "Please complete all required pricing fields",
            type: "error",
            show: true,
          });
          scrollToSection("pricing", { userInitiated: true });
          setIsSubmitting(false);
          setSavingSectionId(null);
          return;
        }
      } else if (
        !isOptionalPackageSection(sectionId) &&
        !validateStage(currentTabStage, stageData)
      ) {
        setShowToster({
          message: "Please complete all required fields before proceeding",
          type: "error",
          show: true,
        });
        setIsSubmitting(false);
        return;
      }

      // Calculate next stage
      const savedStage = Number(generalInfoData?.current_stage) || 0;
      const nextStage = getNextStage(currentTabStage);
      const isAtProgressFrontier = currentTabStage >= savedStage;
      const highestCompletedStage = isAtProgressFrontier
        ? nextStage
        : savedStage;

      const isPublishing = sectionId === "faq" && !skipAutoAdvance;

      // Common mutation data
      const commonData = {
        current_stage: highestCompletedStage,
        is_draft: isPublishing ? 0 : 1,
        package_id: effectivePackageId,
      };

      // Handle mutations for each stage
      switch (sectionId) {
        case "general": {
          const formData = new FormData();
          const isUpdate = generalInfoData.isUpdate;
          const skipGeneralKeys = new Set([
            "isUpdate",
            "current_stage",
            "is_draft",
            "status",
            "images",
            "package_activities",
            "package_days_each_destinations",
            "suitable_ids",
          ]);

          Object.entries(commonData).forEach(([key, value]) => {
            formData.append(key, value);
          });

          Object.entries(generalInfoData).forEach(([key, value]) => {
            if (skipGeneralKeys.has(key)) return;

            if (
              generalInfoData.tour_type === "private" &&
              [
                "starting_location",
                "starting_location_latitude",
                "starting_location_longitude",
                "starting_location_place_id",
              ].includes(key)
            ) {
              formData.append(key, "");
              return;
            }

            if (value !== undefined && value !== null) {
              formData.append(key, value);
            }
          });

          if (generalInfoData.package_activities) {
            formData.append(
              "package_activities",
              JSON.stringify(generalInfoData.package_activities)
            );
          }

          if (generalInfoData.package_days_each_destinations) {
            formData.append(
              "package_days_each_destinations",
              JSON.stringify(
                generalInfoData.package_days_each_destinations.map((item) => ({
                  ...item,
                  location: item.location || "",
                  no_of_days: item.no_of_days || 0,
                  location_latitude: item.location_latitude || "",
                  location_longitude: item.location_longitude || "",
                }))
              )
            );
          }

          if (generalInfoData.suitable_ids?.length) {
            generalInfoData.suitable_ids.forEach((id) => {
              formData.append("suitable_ids[]", id);
            });
          }

          if (generalInfoData.package_group_id) {
            formData.set("package_group_type", "existing");
            formData.set("package_group_id", generalInfoData.package_group_id);
          } else {
            formData.set("package_group_type", "new");
          }

          const totalDays = Number(generalInfoData.total_days);
          if (Number.isFinite(totalDays) && totalDays > 0) {
            const totalNights =
              generalInfoData.total_nights !== "" &&
              generalInfoData.total_nights != null
                ? Number(generalInfoData.total_nights)
                : Math.max(0, totalDays - 1);
            formData.set("total_nights", totalNights);
          }

          if (generalInfoData.images?.length) {
            let newFileIndex = 0;
            const imageOrder = generalInfoData.images.map((img) => {
              if (img?.id) {
                return { type: "existing", id: img.id };
              }
              const orderItem = { type: "new", index: newFileIndex };
              newFileIndex += 1;
              return orderItem;
            });
            formData.append("image_order", JSON.stringify(imageOrder));

            let uploadIndex = 0;
            generalInfoData.images.forEach((img) => {
              if (img?.id) return;
              const file = img?.file || img;
              if (file instanceof File || file instanceof Blob) {
                formData.append(`images[${uploadIndex}]`, file);
                uploadIndex += 1;
              }
            });
          }

          formData.set("status", isPublishing ? 1 : 2);
          formData.set("is_draft", isPublishing ? 0 : 1);

          if (isUpdate) {
            await updateGeneralInfoMutation.mutateAsync({
              packageId: effectivePackageId,
              formData,
            });
          } else {
            await generalInfoMutation.mutateAsync(formData);
          }
          break;
        }
        case "itinerary": {
          const result = await itineraryRef.current?.saveFromFooter?.();
          if (!result?.success) {
            throw new Error(
              result?.message || "Please complete the itinerary day before saving"
            );
          }
          if (!result.allDaysCompleted) {
            clearSectionDirty(sectionId);
            setLastSavedAt(new Date());
            setShowToster({
              message: "Itinerary day saved successfully",
              type: "success",
              show: true,
            });
            return true;
          }
          clearSectionDirty(sectionId);
          setLastSavedAt(new Date());
          return true;
        }
        case "stay": {
          const result = await stayRef.current?.saveFromFooter?.();
          if (!result?.success) {
            throw new Error(
              result?.message || "Please complete stay information before saving"
            );
          }

          const stay_category_ids =
            result.stay_category_ids ||
            [
              ...new Set(
                (stayData?.data || []).map((stay) =>
                  Number(stay.stay_category_id)
                )
              ),
            ];

          await stayMutation.mutateAsync({
            ...commonData,
            stay_category_ids,
          });
          break;
        }
        case "inclusion":
          if (
            inclusionData.masterHighlightsAvailable !== false &&
            !inclusionData.inclusion_highlights?.length
          ) {
            throw new Error("Please select at least one inclusion highlight");
          }
          const processedInclusionData = {
            ...inclusionData,
            ...commonData,
            exclusions: inclusionData.exclusions || [],
            things_to_carry: inclusionData.things_to_carry || [],
            detailed_inclusions: inclusionData.detailed_inclusions || [],
          };

          if (inclusionData.isUpdate) {
            await updateInclusionMutation.mutateAsync({
              packageId: effectivePackageId,
              formData: processedInclusionData,
            });
          } else {
            await inclusionMutation.mutateAsync(processedInclusionData);
          }
          break;
        case "pricing":
          if (pricingRef.current?.saveAllBasePrices) {
            await pricingRef.current.saveAllBasePrices();
          }
          // calculate lowest base price to get default stay category id
          const lowestBasePrice = pricingData.categories.reduce(
            (min, category) => {
              return Math.min(min, category.starting_price);
            },
            Infinity
          );
          const defaultStayCategoryId = pricingData.categories.find(
            (category) => category.starting_price === lowestBasePrice
          )?.stay_category_id;

          await saveAndContinuePricingMutation.mutateAsync({
            ...commonData,
            discount: pricingData.payment_terms.discount_percentage,
            payment_type:
              pricingData.payment_terms.partial_amount_percentage === 100
                ? "full"
                : "partial",
            advance_percentage:
              pricingData.payment_terms.partial_amount_percentage,
            pay_before: pricingData.payment_terms.payment_days,
            default_stay_category_id: defaultStayCategoryId,
          });
          break;
        case "additional":
          const additionalFd = new FormData();
          additionalFd.append(
            "additional_info",
            additionalInfoData?.additional_info || ""
          );
          Object.entries(commonData).forEach(([key, value]) => {
            additionalFd.append(key, value);
          });
          await updateAdditionalInfoMutation.mutateAsync({
            packageId: effectivePackageId,
            formData: additionalFd,
          });
          break;
        case "terms":
          const termsFd = new FormData();
          termsFd.append(
            "terms_and_conditions",
            termsConditionData?.terms_and_conditions || ""
          );
          Object.entries(commonData).forEach(([key, value]) => {
            termsFd.append(key, value);
          });
          await updateTermsandConditionMutation.mutateAsync({
            packageId: effectivePackageId,
            formData: termsFd,
          });
          break;
        case "cancellation":
          if (validateStage(STAGES.CANCELLATION, cancellationData)) {
            await cancellationMutation.mutateAsync({
              packageId: effectivePackageId,
              data: { ...cancellationData, ...commonData },
            });
          } else {
            await advancePackageStage(
              highestCompletedStage,
              commonData.is_draft
            );
          }
          break;
        case "faq":
          const faqPayload = {
            ...commonData,
            ...faqData,
          };

          if (!faqData.is_updated) {
            await addFaqMutation.mutateAsync({
              packageId: effectivePackageId,
              obj: faqPayload,
            });
          } else {
            await updateFaqMutation.mutateAsync({
              packageId: effectivePackageId,
              obj: faqPayload,
            });
          }
          break;
        default:
          console.warn(`Unhandled tab type: ${activeTab}`);
          return false;
      }

      // Advance to the next section after save unless this is a draft-only save
      // (unsaved-dialog flow) or the final FAQ step.
      const shouldAdvanceSection =
        !skipAutoAdvance &&
        currentTabStage < STAGES.FAQ &&
        Boolean(getNextSectionId(sectionId));

      if (highestCompletedStage > savedStage || savedStage < STAGES.FAQ) {
        setGeneralInfoData((prev) => ({
          ...prev,
          current_stage: Math.max(
            Number(prev?.current_stage) || 0,
            highestCompletedStage
          ),
        }));
      }

      clearSectionDirty(sectionId);
      setLastSavedAt(new Date());
      setValidationErrors(null);
      setShowToster({
        message: `${
          navItems.find((item) => item.id === sectionId)?.name || "Section"
        } saved successfully`,
        type: "success",
        show: true,
      });

      if (!skipAutoAdvance && currentTabStage === STAGES.FAQ) {
        performClose();
      } else if (shouldAdvanceSection) {
        const nextSectionId = getNextSectionId(sectionId);
        if (nextSectionId) {
          scrollToSection(nextSectionId, { userInitiated: true });
        }
      }

      return true;
    } catch (error) {
      console.error("Error:", error);
      const apiMessage = error.response?.data?.message;
      const apiErrors = error.response?.data?.errors;
      let message = error.message || "An unexpected error occurred";

      if (typeof apiMessage === "string" && apiMessage) {
        try {
          const parsed = JSON.parse(apiMessage);
          if (parsed && typeof parsed === "object") {
            message = Object.values(parsed).flat().join(", ");
          } else {
            message = apiMessage;
          }
        } catch {
          message = apiMessage;
        }
      } else if (apiMessage && typeof apiMessage === "object") {
        message = Object.values(apiMessage).flat().join(", ");
      } else if (apiErrors) {
        message = Object.values(apiErrors).flat().join(", ");
      }

      setShowToster({
        message,
        type: "error",
        show: true,
      });
      if (error.response?.status === 422) {
        setValidationErrors(apiMessage || apiErrors);
      }
      return false;
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setSavingSectionId(null);
      window.setTimeout(() => {
        isScrollingRef.current = false;
        if (pinnedSectionRef.current === sectionId) {
          pinnedSectionRef.current = null;
        }
        if (!isEditableElement(document.activeElement)) {
          editingSectionRef.current = null;
          setEditingSectionId(null);
        }
      }, 400);
    }
  };

  saveBeforeLeaveRef.current = () =>
    handleSave(displaySectionId, { skipAutoAdvance: true });

  const handleUnsavedSaveAndContinue = async () => {
    const saved = await handleSave(displaySectionId, { skipAutoAdvance: true });
    if (saved) {
      completePendingNavigation();
    }
  };

  const renderSectionCard = (item, children) => {
    const isCompleted = isSectionCompleted(completedStage, item.stage);
    const isDisabledByNoPackage = !generalSectionSaved && item.id !== "general";
    const isDisabledByStage =
      !isPackageSectionAccessible(
        item.stage,
        completedStage,
        generalSectionSaved
      ) && item.id !== "general";
    const isDisabled = isDisabledByNoPackage || isDisabledByStage;
    const isCollapsed = collapsedSections.includes(item.id);
    const isActive = displaySectionId === item.id;
    const canCollapse = item.defaultCollapsed;
    const hideSectionTooltip = [
      "additional",
      "terms",
      "cancellation",
      "faq",
    ].includes(item.id);

    return (
      <section
        key={item.id}
        id={`section-${item.id}`}
        data-section-id={item.id}
        ref={(element) => {
          sectionRefs.current[item.id] = element;
        }}
        className={`scroll-mt-24 rounded-lg border bg-white transition-colors duration-200 [overflow-anchor:none] ${
          isActive
            ? "border-2 border-primary shadow-sm"
            : "border-gray-200"
        }`}
      >
        <button
          type="button"
          onClick={() => {
            if (canCollapse) {
              setCollapsedSections((prev) =>
                prev.includes(item.id)
                  ? prev.filter((id) => id !== item.id)
                  : [...prev, item.id]
              );
            }
            handleSectionNavClick(item.id, item.stage);
          }}
          className={`w-full px-4 py-3.5 text-left sm:px-5 ${
            !isCollapsed ? "border-b border-gray-100" : ""
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                isCompleted
                  ? "bg-emerald-50 text-emerald-600"
                  : isActive
                  ? "bg-primary/10 text-primary"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {isCompleted ? (
                <i className="fi fi-rr-check text-[10px]"></i>
              ) : (
                item.stage
              )}
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3
                className={`truncate text-sm font-bold ${
                  isActive ? "text-gray-900" : "text-gray-800"
                }`}
              >
                {item.name}
              </h3>
              {!hideSectionTooltip && (
                <Tooltip
                  message={getSectionTooltipMessage(item)}
                  position="bottom"
                  maxWidth="400px"
                >
                  <span
                    className="inline-flex shrink-0 cursor-help items-center justify-center text-gray-400 transition hover:text-gray-600"
                    aria-label={`About ${item.name}`}
                  >
                    <i className="fi fi-rr-info text-xs"></i>
                  </span>
                </Tooltip>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {!item.essential && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Optional
                </span>
              )}
              {isDisabled && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-600">
                  <i className="fi fi-rr-lock text-[9px]"></i>
                  Locked
                </span>
              )}
              {canCollapse && (
                <i
                  className={`fi ${
                    isCollapsed ? "fi-rr-angle-down" : "fi-rr-angle-up"
                  } text-xs text-gray-400`}
                ></i>
              )}
            </div>
          </div>
        </button>

        {!isCollapsed && (
          <div className="relative px-4 py-5 sm:px-5 sm:py-6">
            {isDisabled && (
              <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
                <i className="fi fi-rr-info relative top-0.5 text-amber-600"></i>
                <span>
                  {isDisabledByNoPackage
                    ? "Save General Information first to unlock this section."
                    : "Complete the previous sections to unlock this section."}
                </span>
              </div>
            )}
            <div
              className={isDisabled ? "pointer-events-none opacity-45" : ""}
              {...getSectionCaptureProps(item.id)}
            >
              {enhanceSectionChild(item.id, children)}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
    <div className="min-h-full bg-slate-50">
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <i className="fi fi-rr-arrow-left relative top-0.5"></i>
              <span>{backLabel}</span>
            </button>
            <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">
              {effectivePackageId ? "Edit Package" : "New Package"}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-3 sm:gap-4">
            {effectivePackageId ? (
              <PackageItineraryDownload
                packageId={effectivePackageId}
                packageName={generalInfoData?.name}
              />
            ) : null}
            <PackageCompletionTracker
              completedStage={completedStage}
              compact
              wide
            />
          </div>
        </div>

        <div className="border-t border-gray-100 px-4 py-2 lg:hidden">
          <PackageBuilderNav
            activeSectionId={displaySectionId}
            completedStage={completedStage}
            hasPackageId={generalSectionSaved}
            onSectionClick={handleSectionNavClick}
            orientation="horizontal"
          />
        </div>
      </div>

      <div className="mx-auto flex w-full gap-5 px-4 py-4 sm:px-6 lg:py-5">
        <aside className="relative z-20 hidden w-72 shrink-0 lg:block">
          <div className="sticky top-[3.25rem]">
            <div className="rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm">
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Sections
              </p>
              <PackageBuilderNav
                activeSectionId={displaySectionId}
                completedStage={completedStage}
                hasPackageId={generalSectionSaved}
                onSectionClick={handleSectionNavClick}
              />
            </div>
          </div>
        </aside>

        <main ref={contentRef} className="relative min-w-0 flex-1 pb-4">
          {isTabLoading && (
            <div className="absolute inset-0 z-20 flex items-start justify-center bg-slate-50/85 pt-24 backdrop-blur-sm">
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-5 py-4 shadow-lg">
                <svg
                  className="h-6 w-6 animate-spin text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Loading package details...
                </span>
              </div>
            </div>
          )}

          <div className="space-y-8">
          {renderSectionCard(
              navItems[0],
              <GeneralInfo
                draftData={generalInfoData}
                setGeneralInfoData={setGeneralInfoData}
                packageid={effectivePackageId}
                addSubPackageId={addSubPackageId}
                validationErrors={validationErrors}
                onLoadingChange={handleTabLoading}
                restrictedTourType={restrictedTourType}
                selectedGroupPackageCount={selectedGroupPackageCount}
              />
            )}

            {renderSectionCard(
              navItems[1],
              <ItineraryInfo
                ref={itineraryRef}
                packageid={effectivePackageId}
                setItineraryData={setItineraryData}
                onAllDaysCompleted={handleItineraryStageComplete}
                onDaySaved={() => clearSectionDirty("itinerary")}
                onPinSection={pinActiveSection}
                onScrollToDay={scrollToItineraryDay}
                onScrollToItinerarySection={scrollToItinerarySectionTop}
                expectedDayCount={Number(generalInfoData?.total_days) || 0}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[2],
              <StayInfo
                ref={stayRef}
                packageid={effectivePackageId}
                setStayData={setStayData}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[3],
              <Inclusions
                packageid={effectivePackageId}
                setInclusionData={setInclusionData}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[4],
              <PricingInfo
                ref={pricingRef}
                packageid={effectivePackageId}
                setPricingData={setPricingData}
                onLoadingChange={handleTabLoading}
                onPinSection={pinActiveSection}
              />
            )}

            {renderSectionCard(
              navItems[5],
              <AdditionalInfo
                packageid={effectivePackageId}
                setAdditionalInfoData={setAdditionalInfoData}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[6],
              <TermsAndCondition
                packageid={effectivePackageId}
                setTermsConditionData={setTermsConditionData}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[7],
              <CancellationInfo
                packageid={effectivePackageId}
                setCancellationData={setCancellationData}
                onLoadingChange={handleTabLoading}
              />
            )}

            {renderSectionCard(
              navItems[8],
              <Faq
                packageid={effectivePackageId}
                setFAQData={setFAQData}
                onLoadingChange={handleTabLoading}
              />
            )}
          </div>
        </main>
      </div>

      <PackageActionBar
        activeSectionLabel={activeSectionLabel}
        isSubmitting={isSubmitting || savingSectionId !== null}
        isDisabled={isActiveSectionDisabled || noChangesToSave}
        noChangesToSave={noChangesToSave}
        isLastSection={displaySectionId === "faq"}
        lastSavedAt={lastSavedAt}
        onSave={() => handleSave(displaySectionId)}
        onClose={handleClose}
      />

      <UnsavedSectionDialog
        open={unsavedDialogOpen}
        sectionLabel={activeSectionLabel}
        onStay={cancelPendingNavigation}
        onSaveAndContinue={handleUnsavedSaveAndContinue}
        onDiscardNavigation={discardPendingNavigation}
        isSaving={isSubmitting || savingSectionId !== null}
      />

      <UnsavedSectionDialog
        open={leaveDialogOpen}
        sectionLabel={activeSectionLabel}
        onStay={cancelLeave}
        onSaveAndContinue={confirmLeaveSave}
        onDiscardLeave={discardLeave}
        isSaving={isSubmitting || savingSectionId !== null}
        leaveMode
      />

      {showToster && (
        <Toster
          message={showToster.message}
          type={showToster.type}
          onClose={() => setShowToster(false)}
        />
      )}
    </div>
    </DndProvider>
  );
};

export default AddPackage;
